/**
 * Scope chooser for acting on ONE occurrence of a recurring event, a series
 * tail, or the whole series.
 *
 * Shared by delete and edit so the two cannot drift: a user who learns the
 * three options in one place should meet the same wording, ordering and
 * keyboard behaviour in the other.
 *
 * `all` is deliberately never the default. The default is the least
 * far-reaching option, so a mis-click touches one occurrence rather than a
 * standing weekly meeting — which is exactly the failure that motivated
 * putting this in front of EDIT as well as delete: edits were being applied
 * with a hardcoded `scope: 'all'` and no prompt at all, so moving one
 * occurrence silently moved the entire series.
 */

import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import type { RecurrenceEditScope } from '../types';

const OPTIONS: ReadonlyArray<{ value: RecurrenceEditScope; label: string }> = [
  { value: 'this', label: 'This event' },
  { value: 'this_and_future', label: 'This and following events' },
  { value: 'all', label: 'All events' },
];

export interface RecurringScopeDialogProps {
  open: boolean;
  /** Heading — names the action, e.g. "Delete recurring event". */
  heading: string;
  /** Event title, so the user can confirm what they are about to affect. */
  title: string;
  /** Screen-reader question for the radio group. */
  legend: string;
  /** Distinguishes the radio groups when both dialogs exist in one tree. */
  name: string;
  confirmLabel: string;
  /** Styles the confirm button as destructive. Delete sets this; edit does not. */
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: (scope: RecurrenceEditScope) => void;
}

export function RecurringScopeDialog({
  open,
  heading,
  title,
  legend,
  name,
  confirmLabel,
  destructive = false,
  onCancel,
  onConfirm,
}: RecurringScopeDialogProps) {
  const [scope, setScope] = useState<RecurrenceEditScope>('this');

  // Reset on each open. Without this the dialog reopens on whatever the user
  // picked last time — so a previous "All events" silently becomes the default
  // for the next, different, series.
  useEffect(() => {
    if (open) setScope('this');
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent className="max-w-sm">
        <AlertDialogHeader>
          <AlertDialogTitle>{heading}</AlertDialogTitle>
          <AlertDialogDescription className="break-words">{title}</AlertDialogDescription>
        </AlertDialogHeader>

        {/* Native radios rather than a styled widget: this is a keyboard-and-
            screen-reader-critical choice, and the platform control already has
            correct arrow-key and announcement behaviour. */}
        <fieldset className="space-y-1 py-1">
          <legend className="sr-only">{legend}</legend>
          {OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-accent"
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={scope === option.value}
                onChange={() => setScope(option.value)}
                className="h-4 w-4 accent-primary"
              />
              {option.label}
            </label>
          ))}
        </fieldset>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(scope)}
            className={
              destructive
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : undefined
            }
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
